/**
 * HotkeyIndicator — Displays a keyboard shortcut as styled key badges.
 *
 * HotkeyIndicator Component Guidelines
 * - Use beside actions in menus, tooltips, or buttons to surface keyboard shortcuts.
 * - Automatically adapts modifier keys to the user's platform (Cmd on Mac, Ctrl on
 *   Windows/Linux) — no need to handle platform differences manually.
 * - Pass a `title` to override the hotkey's default display name when the surrounding
 *   context already provides a label (e.g. inside a MenuItem that already says "Save").
 */
import type { LayoutClassName } from "@replo/design-system/types/layoutClassName";
import type {
  Hotkey,
  HotkeyIndicatorCharacter,
  HotkeyMetaKey,
} from "@replo/design-system/utils/hotkeys";

import React from "react";

import { Badge } from "@replo/design-system/components/badge/Badge";
import {
  getHotkeyData,
  HotkeyMetaKeyToLabel,
} from "@replo/design-system/utils/hotkeys";
import { isMacintosh } from "@replo/design-system/utils/platform";
import twMerge from "@replo/design-system/utils/twMerge";

interface HotkeyIndicatorProps<ClassName extends string = string> {
  hotkey: Hotkey;
  title?: string;
  className?: LayoutClassName<ClassName>;
  UNSAFE_className?: string;
}

function HotkeyIndicator<ClassName extends string>({
  hotkey,
  title,
  className,
  UNSAFE_className,
}: HotkeyIndicatorProps<ClassName>) {
  const hotkeyData = getHotkeyData(hotkey);
  const keys = resolveKeyLabels(hotkeyData.indicatorCharacters);

  return (
    <div
      data-slot="hotkey-indicator"
      className={twMerge(
        "flex flex-row items-center justify-center gap-1 text-xs",
        className,
        UNSAFE_className,
      )}
    >
      <span>{title ?? hotkeyData.displayName}</span>
      <div className="flex flex-row items-center gap-[2px]">
        {keys.map(({ id, label }) => (
          <Badge
            key={id}
            size="xs"
            className="bg-sheet text-placeholder w-auto px-0.5"
          >
            {label}
          </Badge>
        ))}
      </div>
    </div>
  );
}

function resolveKeyLabels(characters: HotkeyIndicatorCharacter[]) {
  const isMac = isMacintosh();
  return characters.map((character) => {
    const mapped = HotkeyMetaKeyToLabel[character as HotkeyMetaKey];
    if (mapped) {
      if (typeof mapped === "string") {
        return { id: character, label: mapped };
      }
      const label = isMac ? mapped.mac : mapped.windows;
      return { id: character, label };
    }
    return { id: character, label: character.toUpperCase() };
  });
}

export { HotkeyIndicator };
